
import React, { useState } from 'react';
import { Bookmark, MessageSquare, FileText, Clock, ChevronRight, Star, AlertCircle, Zap, BarChart3, Inbox, Settings, Search, Briefcase } from 'lucide-react';
import { Link } from 'react-router-dom';
import { MOCK_CONSULTANCIES, MOCK_CLIENT_SPEND_DATA, MOCK_RFQS, MOCK_PROPOSALS } from '../constants';
import { CommunicationHub } from './shared/CommunicationHub';
import { SettingsView } from './shared/SettingsView';
import { ComparisonTable } from './client/ComparisonTable';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { RFQDetail } from './shared/RFQDetail';
import { RFQ } from '../types';

export const UserDashboard: React.FC = () => {
  const [activeView, setActiveView] = useState<'overview' | 'rfqs' | 'shortlist' | 'intelligence' | 'inbox' | 'settings'>('overview');
  const [selectedRFQ, setSelectedRFQ] = useState<RFQ | null>(null);
  
  const savedConsultants = MOCK_CONSULTANCIES.slice(0, 2);

  return (
    <div className="flex h-screen bg-slate-50">
        {/* Sidebar */}
        <div className="w-64 bg-white border-r border-slate-200 flex flex-col flex-shrink-0">
            <div className="p-6 border-b border-slate-200">
                <h1 className="font-bold text-xl text-slate-900">Client Portal</h1>
                <p className="text-xs text-slate-500 mt-1">Startup Rx</p>
            </div>
            <nav className="flex-grow p-4 space-y-1 overflow-y-auto">
                <button onClick={() => { setActiveView('overview'); setSelectedRFQ(null); }} className={`w-full flex items-center px-4 py-3 text-sm font-medium rounded-lg ${activeView === 'overview' ? 'bg-blue-50 text-blue-700' : 'text-slate-600 hover:bg-slate-50'}`}>
                    <Briefcase className="w-5 h-5 mr-3" /> Overview
                </button>
                <button onClick={() => { setActiveView('rfqs'); setSelectedRFQ(null); }} className={`w-full flex items-center px-4 py-3 text-sm font-medium rounded-lg ${activeView === 'rfqs' ? 'bg-blue-50 text-blue-700' : 'text-slate-600 hover:bg-slate-50'}`}>
                    <FileText className="w-5 h-5 mr-3" /> My RFQs
                </button>
                <button onClick={() => { setActiveView('shortlist'); setSelectedRFQ(null); }} className={`w-full flex items-center px-4 py-3 text-sm font-medium rounded-lg ${activeView === 'shortlist' ? 'bg-blue-50 text-blue-700' : 'text-slate-600 hover:bg-slate-50'}`}>
                    <Star className="w-5 h-5 mr-3" /> Shortlists
                </button>
                <button onClick={() => { setActiveView('inbox'); setSelectedRFQ(null); }} className={`w-full flex items-center px-4 py-3 text-sm font-medium rounded-lg ${activeView === 'inbox' ? 'bg-blue-50 text-blue-700' : 'text-slate-600 hover:bg-slate-50'}`}>
                    <Inbox className="w-5 h-5 mr-3" /> Inbox & Tasks
                </button>
                <button onClick={() => { setActiveView('intelligence'); setSelectedRFQ(null); }} className={`w-full flex items-center px-4 py-3 text-sm font-medium rounded-lg ${activeView === 'intelligence' ? 'bg-blue-50 text-blue-700' : 'text-slate-600 hover:bg-slate-50'}`}>
                    <BarChart3 className="w-5 h-5 mr-3" /> Intelligence
                </button>
                <button onClick={() => { setActiveView('settings'); setSelectedRFQ(null); }} className={`w-full flex items-center px-4 py-3 text-sm font-medium rounded-lg ${activeView === 'settings' ? 'bg-blue-50 text-blue-700' : 'text-slate-600 hover:bg-slate-50'}`}>
                    <Settings className="w-5 h-5 mr-3" /> Settings
                </button>
            </nav>
        </div>

        {/* Main Content Area */}
        <div className="flex-grow overflow-y-auto p-8">
            
            {activeView === 'overview' && (
                <div className="space-y-8">
                    <div className="flex justify-between items-end">
                        <div>
                            <h2 className="text-2xl font-bold text-slate-900">Dashboard Overview</h2>
                            <p className="text-slate-500">Welcome back, Mike.</p>
                        </div>
                        <Link to="/client-dashboard/create-rfq" className="inline-flex items-center justify-center px-4 py-2 bg-blue-900 text-white rounded-lg font-medium hover:bg-blue-800 transition-colors">
                            <Zap className="w-4 h-4 mr-2" /> New Automated RFQ
                        </Link>
                    </div>

                    {/* Widgets Grid from original design */}
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                         {/* Active Projects Widget */}
                         <div className="lg:col-span-2 bg-white rounded-lg shadow-sm border border-slate-200 p-6">
                             <h3 className="text-lg font-bold text-slate-900 mb-4">Active Projects</h3>
                             {MOCK_RFQS.slice(0,2).map(rfq => (
                                <div key={rfq.id} className="border rounded-lg p-4 mb-4 hover:bg-slate-50 cursor-pointer" onClick={() => { setActiveView('rfqs'); setSelectedRFQ(rfq); }}>
                                    <div className="flex justify-between mb-2">
                                        <span className="font-medium text-slate-900">{rfq.title}</span>
                                        <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full font-bold uppercase">{rfq.status}</span>
                                    </div>
                                    <div className="flex gap-4 text-sm text-slate-500">
                                        <span><Clock className="w-4 h-4 inline mr-1"/> Posted {rfq.postedDate}</span>
                                        <span><FileText className="w-4 h-4 inline mr-1"/> {rfq.proposalCount} Proposals</span>
                                    </div>
                                </div>
                             ))}
                         </div>
                         
                         {/* Quick Actions Widget */}
                         <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
                             <h3 className="text-lg font-bold text-slate-900 mb-4">Quick Actions</h3>
                             <div className="space-y-2">
                                 <button onClick={() => setActiveView('shortlist')} className="w-full text-left p-2 hover:bg-slate-50 rounded text-sm font-medium text-slate-700">Create Shortlist</button>
                                 <Link to="/directory" className="block w-full text-left p-2 hover:bg-slate-50 rounded text-sm font-medium text-slate-700">Browse Directory</Link>
                             </div>
                         </div>
                    </div>
                </div>
            )}

            {activeView === 'rfqs' && (
                 selectedRFQ ? (
                     <RFQDetail 
                        rfq={selectedRFQ} 
                        userRole="client" 
                        onBack={() => setSelectedRFQ(null)} 
                        existingProposals={MOCK_PROPOSALS.filter(p => p.rfqId === selectedRFQ.id)}
                     />
                 ) : (
                     <div className="space-y-6">
                         <div className="flex justify-between items-center">
                             <h2 className="text-2xl font-bold text-slate-900">My RFQs</h2>
                             <Link to="/client-dashboard/create-rfq" className="bg-blue-600 text-white px-4 py-2 rounded-md font-medium hover:bg-blue-700">New RFQ</Link>
                         </div>
                         {/* Mock RFQ List */}
                         <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
                             <table className="min-w-full divide-y divide-slate-200">
                                 <thead className="bg-slate-50">
                                     <tr>
                                         <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Title</th>
                                         <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Status</th>
                                         <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Proposals</th>
                                         <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Posted</th>
                                     </tr>
                                 </thead>
                                 <tbody className="divide-y divide-slate-200">
                                     {MOCK_RFQS.map(rfq => (
                                         <tr key={rfq.id} className="hover:bg-slate-50 cursor-pointer" onClick={() => setSelectedRFQ(rfq)}>
                                             <td className="px-6 py-4 font-medium text-slate-900">{rfq.title}</td>
                                             <td className="px-6 py-4"><span className={`text-xs px-2 py-1 rounded-full uppercase font-bold ${rfq.status === 'open' ? 'bg-green-100 text-green-800' : 'bg-slate-100 text-slate-800'}`}>{rfq.status}</span></td>
                                             <td className="px-6 py-4 text-slate-500">{rfq.proposalCount} Received</td>
                                             <td className="px-6 py-4 text-slate-500">{rfq.postedDate}</td>
                                         </tr>
                                     ))}
                                 </tbody>
                             </table>
                         </div>
                     </div>
                 )
            )}

            {activeView === 'shortlist' && (
                <div className="space-y-8">
                    <h2 className="text-2xl font-bold text-slate-900">Vendor Shortlists</h2>
                    <ComparisonTable consultancies={MOCK_CONSULTANCIES.slice(0, 3)} />
                </div>
            )}

            {activeView === 'intelligence' && (
                <div className="space-y-8">
                    <div className="flex justify-between items-center">
                         <h2 className="text-2xl font-bold text-slate-900">Project Intelligence</h2>
                         <span className="bg-yellow-100 text-yellow-800 text-xs font-bold px-2 py-1 rounded border border-yellow-200">PREMIUM</span>
                    </div>
                    
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                        <div className="bg-white p-6 rounded-lg border border-slate-200">
                            <h3 className="font-bold text-slate-900 mb-6">Spend Analysis vs Market Benchmark</h3>
                            <div className="h-80">
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={MOCK_CLIENT_SPEND_DATA}>
                                        <CartesianGrid strokeDasharray="3 3" vertical={false}/>
                                        <XAxis dataKey="category"/>
                                        <YAxis/>
                                        <Tooltip/>
                                        <Bar dataKey="spend" fill="#3b82f6" name="My Spend"/>
                                        <Bar dataKey="benchmark" fill="#94a3b8" name="Market Avg"/>
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </div>
                        <div className="bg-white p-6 rounded-lg border border-slate-200">
                            <h3 className="font-bold text-slate-900 mb-4">Efficiency Metrics</h3>
                            <div className="space-y-4">
                                <div className="p-4 bg-slate-50 rounded-lg">
                                    <div className="text-sm text-slate-500">Avg Time to Award</div>
                                    <div className="text-2xl font-bold text-slate-900">14 Days <span className="text-sm font-normal text-green-600">(-2 days vs avg)</span></div>
                                </div>
                                <div className="p-4 bg-slate-50 rounded-lg">
                                    <div className="text-sm text-slate-500">Proposal Variance</div>
                                    <div className="text-2xl font-bold text-slate-900">15% <span className="text-sm font-normal text-slate-400">(Price spread)</span></div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {activeView === 'inbox' && <CommunicationHub />}
            
            {activeView === 'settings' && <SettingsView />}
        </div>
    </div>
  );
};
